import { useCallback } from 'react';
import Select, {
  GroupBase,
  OptionProps,
  MultiValueRemoveProps,
  MultiValueProps,
} from 'react-select';
import { useTreeSelect } from './useTreeSelect';
import SelectLayout from '../SelectLayout';
import Option from './Option';
import TreeSelectMultiValue from './TreeSelectMultiValue';
import MultiValueRemove from '../components/MultiValueRemove';

import styles from '../select.module.scss';
import treeSelectStyles from './treeSelect.module.scss';

import { ITreeSelectOption, TTreeSelectProps } from './interfaces';

export const TreeSelect = <
  SOption extends ITreeSelectOption = ITreeSelectOption,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<SOption> = GroupBase<SOption>,
>({
  components = {},
  dataTest,
  ...selectProps
}: TTreeSelectProps<SOption, IsMulti, Group>) => {
  const {
    options,
    groupedChildrenByParents,
    selectedTreeParents,
    toggleParentOption,
    optionClickHandler,
    multiValueRemoveClickHandler,
    handleInputChange,
    handleFilterOption,
  } = useTreeSelect(selectProps.options as SOption[]);

  const OptionComponent = useCallback(
    (props: OptionProps<SOption>) => (
      <Option
        { ...props }
        component={ components.Option }
        dataTest={ dataTest }
        groupedChildrenByParents={ groupedChildrenByParents }
        toggleParentOption={ toggleParentOption }
        selectedTreeParents={ selectedTreeParents }
        optionClickHandler={ optionClickHandler }
      />
    ), [selectedTreeParents, groupedChildrenByParents, dataTest],
  );

  const MultiValueComponent = useCallback(
    (props: MultiValueProps<SOption>) => (
      <TreeSelectMultiValue
        { ...props }
        component={ components.MultiValue }
        groupedChildrenByParents={ groupedChildrenByParents }
      />
    ), [groupedChildrenByParents],
  );

  const MultiValueRemoveComponent = useCallback(
    (props: MultiValueRemoveProps<SOption>) => (
      <div
        className={ treeSelectStyles.removeIcon }
        onClick={ () => multiValueRemoveClickHandler(props) }
      >
        <MultiValueRemove ownProps={ props } components={ components } />
      </div>
    ), [],
  );

  const props = {
    ...selectProps,
    dataTest,
    menuClassName: styles.treeSelectMenu,
    options: options,
    components: {
      ...components,
      Option: OptionComponent,
      MultiValue: MultiValueComponent,
      MultiValueRemove: MultiValueRemoveComponent,
    },
    handleInputChange,
    handleFilterOption,
  };

  return <SelectLayout { ...props } component={ Select } />;
};

export default TreeSelect;
